/**
 * Winter season drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const winterSeasonDrawingsPack: ToilePack = {
    slug: 'winter-season-drawings',
    category: 'coloring',
    name: 'Winter season drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'snow', label: 'Snowy hills', subject: 'a hut on a snowy hill with pine trees and smoke from the chimney'},
        {key: 'fire', label: 'By the fire', subject: 'children sitting around a fire wearing woollen caps'},
        {key: 'family', label: 'Winter walk', subject: 'a family in sweaters and scarves walking on a misty morning'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a snowman', 'hot tea', 'morning fog'],
    directionHint: 'try: a snowman',
    seo: {
        title: 'Winter Season Drawings: Snowy Hill, Fire, Winter Walk',
        metaDescription: 'Nine winter season drawings: a hut on a snowy hill, children by a fire and a family in sweaters, in pencil, colour and outline.',
        h1: 'Winter season drawings: snowy hills, children by the fire, winter walk',
        tagline: 'Three winter scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Winter season drawings for a competition, a season chart or a class project. The set shows a hut on a snowy hill with pine trees, children sitting round a fire in woollen caps, and a family in sweaters and scarves walking on a misty morning. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a snowman, hot tea or morning fog.',
        useCases: [
            'a seasons chart for a class',
            'a winter scenery drawing for a competition',
            'a classroom display for December',
            'a colouring page for a school project',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Are these scenes from a particular place?', a: 'No. They are generic winter pictures, not a particular town or hill.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
